import { useEffect, useState, useRef } from "react"; // Added useRef
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, Link, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../features/cred/credSlice";
import Loader from "../components/Loader";
import { CLIENT_COMPANY, DEVELOPED_BY } from "../utils/constants";
import { fetchCarListings } from "../features/carListing/carListingSlice";
import { isUserAuthorized } from "../utils/authorize";

const LISTING_TAB_CONFIG = {
  "/car-listing-status": { status: "all" },
  "/car-listing-approved": { status: "approved" },
  "/car-listing-rejected": { status: "rejected" },
  "/car-listing-pending": { status: "pending" },
};

const DEFAULT_LISTING_PATH = "/car-listing-status";

const MasterLayout = ({ children }) => {
  const user = useSelector((state) => state?.cred?.user);
  const role = useSelector((state) => state?.cred?.user?.role);
  const username = useSelector((state) => state?.cred?.user?.username);
  const status = useSelector((state) => state.blog?.status); // Assuming this is for a global loading status

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location; // Destructure pathname for easier use

  const [sidebarActive, seSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Ref for the sidebar menu to avoid re-querying the DOM too often if not needed
  const sidebarMenuRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !sidebarMenuRef.current) return;

    const sidebarMenuElement = sidebarMenuRef.current;

    const handleDropdownClick = (event) => {
      // Check if the clicked element or its parent is a dropdown trigger
      const clickedLink = event.target.closest(".dropdown > a, .dropdown > Link");
      if (!clickedLink) return;

      event.preventDefault(); // Prevent default for <a href="#">
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = sidebarMenuElement.querySelectorAll(".dropdown");
      allDropdowns.forEach((dropdown) => {
        if (dropdown !== clickedDropdown || isActive) { // Don't close the one being opened, or if it's already active (to toggle it)
          dropdown.classList.remove("open");
          const submenu = dropdown.querySelector(".sidebar-submenu");
          if (submenu) {
            submenu.style.maxHeight = "0px";
          }
        }
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      }
    };

    // Attach click event listener to the sidebar menu (event delegation)
    sidebarMenuElement.addEventListener("click", handleDropdownClick);

    const openActiveDropdown = () => {
      const allDropdowns = sidebarMenuElement.querySelectorAll(".dropdown");

      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a"); // <Link> renders <a>
        let isCurrentDropdownActive = false;
        submenuLinks.forEach((link) => {
          // Get the 'href' attribute, which react-router-dom sets on the rendered <a> tag
          const linkPath = link.getAttribute("href");
          if (linkPath === pathname) {
            isCurrentDropdownActive = true;
          }
        });

        const directLink = dropdown.querySelector(":scope > a, :scope > Link"); // Check direct child link
        if (directLink && directLink.getAttribute("href") === pathname && !dropdown.querySelector(".sidebar-submenu")) {
             // If it's a direct link (not a dropdown parent) and active, no need to open a submenu
        } else if (isCurrentDropdownActive) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`;
            }
        } else {
             // If no active sub-link, ensure it's closed (unless it was the one just clicked, handled by handleDropdownClick)
            if (!dropdown.classList.contains('open-by-click')) { // Add a temporary class if needed or rely on handleDropdownClick to manage this
                dropdown.classList.remove("open");
                const submenu = dropdown.querySelector(".sidebar-submenu");
                if (submenu) {
                    submenu.style.maxHeight = "0px";
                }
            }
        }
      });
    };

    openActiveDropdown();

    return () => {
      sidebarMenuElement.removeEventListener("click", handleDropdownClick);
    };
  }, [pathname]); // Rerun when pathname changes

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentSearch = params.get("search") || "";
    setSearchValue(currentSearch);
  }, [location.search]);

  if (status === "loading" && !children) { // More specific loading check
    return <Loader />;
  }

  const sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  const mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmedQuery = searchValue.trim();

    const activeListingPath = LISTING_TAB_CONFIG[pathname]
      ? pathname
      : DEFAULT_LISTING_PATH;
    const activeListing = LISTING_TAB_CONFIG[activeListingPath];

    const searchSuffix = trimmedQuery
      ? `?search=${encodeURIComponent(trimmedQuery)}`
      : "";
    const targetPath = `${activeListingPath}${searchSuffix}`;
    const isOnTargetPath =
      pathname === activeListingPath && location.search === searchSuffix;

    if (!isOnTargetPath) {
      navigate(targetPath);
    } else {
      const activeStatus = activeListing.status || "all";
      const requestStatus = activeStatus === "rejected" ? "rejected" : "all";

      const fetchArgs = {
        page: 1,
        limit: 12,
        status: requestStatus,
        ...(trimmedQuery ? { search: trimmedQuery } : {}),
      };

      if (activeStatus === "rejected") {
        fetchArgs.approvalStatus = "rejected";
      }

      dispatch(fetchCarListings(fetchArgs));
    }

    if (mobileMenu) {
      setMobileMenu(false);
    }
  };

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to='/' className='sidebar-logo'>
            <img
              src='/assets/images/logo-b.svg' // Use absolute path from public or ensure assets are served correctly
              alt='site logo'
              className='light-logo'
            />
            <img
              src='/assets/images/logo.svg'
              alt='site logo'
              className='dark-logo'
            />
            <img
              src='/assets/images/logo.svg'
              alt='site logo'
              className='logo-icon'
            />
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          {/* Pass the ref to the ul element */}
          <ul className='sidebar-menu' id='sidebar-menu' ref={sidebarMenuRef}>
            {/* ... (Your commented out menu items) ... */}

            {/* Users Dropdown - Example */}
            {isUserAuthorized(role) && (
              <li className='dropdown'>
                <Link to='#'> {/* Use '#' for dropdown triggers that don't navigate directly */}
                  <Icon
                    icon='flowbite:users-group-outline'
                    className='menu-icon'
                  />
                  <span>Users</span>
                </Link>
                <ul className='sidebar-submenu'>
                  <li>
                    <Link
                      to='/users-list'
                      className={pathname === "/users-list" ? "active-page" : ""}
                    >
                      <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                      Users List
                    </Link>
                  </li>
             
                  <li>
                    <Link
                      to='/add-user'
                      className={pathname === "/add-user" ? "active-page" : ""}
                    >
                      <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                      Add User
                    </Link>
                  </li>
                
                </ul>
              </li>
            )}

            {/* Blog side tab */}

            <li className='dropdown'>
              <Link to='#'>
                <Icon
                  icon='material-symbols:article-outline-rounded'
                  className='menu-icon'
                />
                <span>Blogs</span>
              </Link>
              <ul className='sidebar-submenu'>
                <li>
                  <Link
                    to='/blogs-list'
                    className={pathname === "/blogs-list" ? "active-page" : ""}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    All Blogs
                  </Link>
                </li>
                <li>
                  <Link
                    to='/add-blog'
                    className={pathname === "/add-blog" ? "active-page" : ""}
                  >
                    <i className='ri-circle-fill circle-icon text-success-600 w-auto' />{" "}
                    Add Blog
                  </Link>
                </li>
              </ul>
            </li>

            <li className='sidebar-menu-group-title'>Application</li>

            {/* Property Dropdown */}
            <li className='dropdown'>
              <Link to='#'>
                <Icon
                  icon='mdi:view-dashboard-outline'
                  className='menu-icon'
                />
                <span>Dashboard</span>
              </Link>
              <ul className='sidebar-submenu'>
                <li>
                  <Link
                    to='/dashboard'
                    className={pathname === "/dashboard" ? "active-page" : ""}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    All Dashboard
                  </Link>
                </li>
              </ul>
            </li>

            {/* Announcement Dropdown */}
            <li className='dropdown'>
              <Link to='#'> {/* Use '#' for dropdown triggers */}
                <Icon
                  icon='mdi:car-outline'
                  className='menu-icon'
                />
                <span>Listing Cars</span>
              </Link>
              <ul className='sidebar-submenu'>
                <li>
                  <Link
                    to='/car-listing-status'
                    className={pathname === "/car-listing-status" ? "active-page" : ""}
                  >
                    <i className='ri-list-check circle-icon text-cyan w-auto text-lg' />{" "}
                    All Listings
                  </Link>
                </li>
                <li>
                  <Link
                    to='/car-listing-approved'
                    className={pathname === "/car-listing-approved" ? "active-page" : ""}
                  >
                    <i className='ri-checkbox-circle-line circle-icon text-primary-600 w-auto text-lg' />{" "}
                    Approved
                  </Link>
                </li>
                <li>
                  <Link
                    to='/car-listing-rejected'
                    className={pathname === "/car-listing-rejected" ? "active-page" : ""}
                  >
                    <i className='ri-close-circle-line circle-icon text-danger-main w-auto text-lg' />{" "}
                    Rejected
                  </Link>
                </li>
                <li>
                  <Link
                    to='/car-listing-pending'
                    className={pathname === "/car-listing-pending" ? "active-page" : ""}
                  >
                    <i className='ri-time-line circle-icon text-info-main w-auto text-lg' />{" "}
                    Pending
                  </Link>
                </li>
                <li>
                  <Link
                    to='/car-listing-booked'
                    className={pathname === "/car-listing-booked" ? "active-page" : ""}
                  >
                    <i className='ri-book-line circle-icon text-info w-auto text-lg' />{" "}
                    Booked
                  </Link>
                </li>
              </ul>
            </li>
            {/* Join Our Team Dropdown */}
            <li className='dropdown'>
              <Link to='#'>
                <Icon
                  icon='mdi:code-braces-box'
                  className='menu-icon'
                />
                <span>Constants</span>
              </Link>
              <ul className='sidebar-submenu'>
                {/* <li>
                  <Link
                    to='/all_constants'
                    className={pathname === "/all_constants" ? "active-page" : ""}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    All Constants
                  </Link>
                </li> */}
                <li>
                  <Link
                    to='/price-range'
                    className={pathname === "/price-range" ? "active-page" : ""}
                  >
                    <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                    Price Range
                  </Link>
                </li>
                
              </ul>
            </li>
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
                <form className='navbar-search' onSubmit={handleSearchSubmit}>
                  <input
                    type='text'
                    name='search'
                    placeholder='Search'
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                  />
                  <button
                    type='submit'
                    className='navbar-search-submit'
                    aria-label='Search car listings'
                  >
                    <Icon icon='ion:search-outline' className='icon' />
                  </button>
                </form>
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                <ThemeToggleButton />
                {/* ... (other navbar items) ... */}
                {user !== null && (
                  <div className='dropdown'>
                    <button
                      className='d-flex justify-content-center align-items-center rounded-circle'
                      type='button'
                      data-bs-toggle='dropdown' // This relies on Bootstrap's JS for dropdowns
                    >
                      <img
                        src='/assets/images/user.png' // Use absolute path
                        alt='image_user'
                        className='w-40-px h-40-px object-fit-cover rounded-circle'
                      />
                    </button>
                    <div className='dropdown-menu to-top dropdown-menu-sm'>
                      <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                        <div>
                          <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                            {username?.slice(0, 1).toUpperCase() +
                              username?.slice(1)}
                          </h6>
                          <span className='text-secondary-light fw-medium text-sm'>
                            {isUserAuthorized(role) ? "Super Admin" : "Admin"}
                          </span>
                        </div>
                        {/* No close button needed here as Bootstrap handles it */}
                      </div>
                      <ul className='to-top-list list-unstyled mb-0'> {/* Added list-unstyled and mb-0 for Bootstrap */}
                        <li>
                          <Link
                            className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3'
                            to='/view-profile'
                          >
                            <Icon
                              icon='solar:user-linear'
                              className='icon text-xl'
                            />{" "}
                            My Profile
                          </Link>
                        </li>
                        <li>
                          <Link
                            className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                            to='#'
                            onClick={() => dispatch(logoutUser())}
                          >
                            <Icon
                              icon='lucide:power'
                              className='icon text-xl'
                            />{" "}
                            Log Out
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-main-body'>
          {status === "loading" && children ? <Loader /> : children}
        </div>

        <footer className='d-footer'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <p className='mb-0'>
                © {new Date().getFullYear()} {CLIENT_COMPANY}. All
                Rights Reserved.
              </p>
            </div>
            <div className='col-auto'>
              <p className='mb-0'>
                Made by <span className='text-primary-600'>{DEVELOPED_BY}</span>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
